import { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { Collapse } from 'react-bootstrap';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import {
  AppDatePicker,
  AppFileUploader,
  AppInput,
  AppInputNumber,
  AppModal,
  AppNoInfo,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
} from '@/layout/AppComponents';
import { toFormData } from '../../../minions/forms';
import ModuleComsaSideBarReports from './ModuleComsaSideBarReports';
import { renderCommaFloat } from '../../../minions/int';
import Translator from '../../../layout/components/helpers/Translator';
import Observations from '../../../layout/screenSpecifics/Modules/ComsaReports/Observations';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import { axiosPath } from '../../../routing/helpers/router';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import _ from 'lodash';
import {
  getFile,
  handleDisabled,
  handleMinDate,
} from '../../../layout/screenSpecifics/Modules/ComsaReports/tools/comsaReportsTools';
import errorHandler from '@/layout/components/error/ErrorHandler';

const ModuleComsaPowerUseReportScreen = () => {
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const global = useSelector((state) => state.global);
  const [loading, setLoading] = useState(true);
  const [reportList, setReportList] = useState([]);
  const [showSendReportModal, setShowSendReportModal] = useState(false);
  const [isSendingReport, setIsSendingReport] = useState(false);
  const [reportFormInitialValues, setReportFormInitialValues] = useState(null);
  const [months] = useState(
    Array.from({ length: 12 }, (_, i) => ({
      label: <Translator file="user" name={`calendar.month${i + 1}`} />,
      value: i + 1,
    })),
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReportId, setDeleteReportId] = useState();
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
  const [reportType] = useState('powerUse');
  const [filters, setFilters] = useState({
    month: null,
    year: null,
    kwUseFrom: null,
    kwUseTo: null,
    provider: null,
  });
  const [filterMonth, setFilterMonth] = useState(null);
  const [filterYear, setFilterYear] = useState(null);
  const [filterKwUseFrom, setFilterKwUseFrom] = useState(null);
  const [filterKwUseTo, setFilterKwUseTo] = useState(null);
  const [filterProvider, setFilterProvider] = useState(null);
  const [actualFilters, setActualFilters] = useState({
    month: null,
    year: null,
    kwUseFrom: null,
    kwUseTo: null,
    provider: null,
  });
  const [moduleData, setModuleData] = useState(null);

  // > FILTERS
  const handleFilters = () => {
    const newFilters = {
      month: filterMonth,
      year: filterYear,
      kwUseFrom: filterKwUseFrom,
      kwUseTo: filterKwUseTo,
      provider: filterProvider,
    };
    if (!_.isEqual(newFilters, actualFilters)) {
      setActualFilters(newFilters);
      setLoading(true);
      setFilters(newFilters);
    }
  };
  const handleRemoveFilters = () => {
    const newFilters = {
      month: null,
      year: null,
      kwUseFrom: null,
      kwUseTo: null,
      provider: null,
    };
    setFilterMonth(null);
    setFilterYear(null);
    setFilterKwUseFrom(null);
    setFilterKwUseTo(null);
    setFilterProvider(null);
    setActualFilters(newFilters);
    setLoading(true);
    setFilters(newFilters);
  };
  // < FILTERS

  // > LOAD REPORTS //
  const loadReports = useCallback(async () => {
    await axios.post(
        axiosPath('api_r_user_module_comsaReports_report'),
        {
          type: reportType,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
          filters,
        },
      )
      .then(({ data }) => {
        if (isMounted.current) {
          setReportList(data);
          setIsFiltersPanelOpen(false);
          setLoading(false);
        }
      })
      .catch((err) => {
        dispatch(errorHandler(err));
      });
  }, [filters, reportType, moduleBridgeHex, subModuleBridgeHex, plantHex]);

  useEffect(() => {
    if (loading) {
      loadReports();
    }
  }, [loading, loadReports]);
  // < LOAD REPORTS //

  // < ADD REPORT
  const addReport = async (values) => {
    try {
      const response = await axios.post(
        axiosPath('api_r_user_module_comsaReports_addReport'),
        toFormData({
          ...values,
          year: values.year,
          type: reportType,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
        }),
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data;
    } catch (err) {}
  };

  const handleAddReport = () => {
    setShowSendReportModal(true);
    setReportFormInitialValues({
      id: null,
      month: null,
      year: null,
      kwUse: '',
      provider: '',
      files: null,
      deletedFiles: [],
      comment: '',
    });
  };
  // < ADD REPORT

  // < EDIT REPORT
  const editReport = async (values) => {
    try {
      const response = await axios.post(
        axiosPath('api_r_user_module_comsaReports_editReport'),
        toFormData({
          ...values,
          year: values.year,
          type: reportType,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
        }),
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data;
    } catch (err) {}
  };

  const handleEditReport = (report) => {
    setReportFormInitialValues({
      ...report,
      month: Number(report.month),
      year: report.year,
      deletedFiles: [],
      comment: report.comment,
    });
    setShowSendReportModal(true);
  };

  const handleDeletedFiles = (setFieldValue, deletedFiles, salt) => {
    if (deletedFiles.includes(salt)) {
      setFieldValue(
        'deletedFiles',
        deletedFiles.filter((s) => s !== salt),
      );
    } else setFieldValue('deletedFiles', [...deletedFiles, salt]);
  };
  // < EDIT REPORT

  // < DELETE REPORT
  const removeReport = useCallback(async () => {
    try {
      const response = await axios.post(
        axiosPath('api_r_user_module_comsaReports_removeReport'),
        {
          id: deleteReportId,
          type: reportType,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
        },
      );
      return response.data;
    } catch (err) {}
  });

  useEffect(() => {
    if (deleteReportId) setShowDeleteModal(true);
  }, [deleteReportId]);

  const handleOnDeleteModalConfirm = () => {
    removeReport().then((res) => {
      setReportList(reportList.filter((r) => r.id !== res.id));
      setDeleteReportId(null);
      setShowDeleteModal(false);
    });
  };

  const handleOnDeleteModalClose = () => {
    setDeleteReportId(null);
    setShowDeleteModal(false);
  };
  // < DELETE REPORT

  // < FORM SCHEMA
  const validationSchema = Yup.object().shape({
    month: Yup.number()
      .nullable()
      .required(
        ReactDOMServer.renderToString(
          <Translator file="user" name="user.required" />,
        ),
      ),
    year: Yup.date()
      .nullable()
      .required(
        ReactDOMServer.renderToString(
          <Translator file="user" name="user.required" />,
        ),
      ),
    kwUse: Yup.string()
      .matches(
        /^\d+(\.\d{1,3})?$/,
        ReactDOMServer.renderToString(
          <Translator file="module-comsaReports" name="0.000.format" />,
        ),
      )
      .required(
        ReactDOMServer.renderToString(
          <Translator file="user" name="user.required" />,
        ),
      ),
    provider: Yup.string().required(
      ReactDOMServer.renderToString(<Translator file="user" name="user.required" />),
    ),
  });
  // < FORM SCHEMA

  // < SAVE FORM
  const handleSaveReport = (values) => {
    setIsSendingReport(true);
    const reportFormFunction = values.id ? editReport(values) : addReport(values);
    reportFormFunction
      .then(() => {
        if (isMounted.current) {
          setShowSendReportModal(false);
          setLoading(true);
        }
      })
      .finally(() => {
        if (isMounted.current) setIsSendingReport(false);
      });
  };
  // < SAVE FORM

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    await axios
      .post(axiosPath('api_r_user_module_comsaReports_getModuleData'), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then(({ data }) => {
        if (isMounted.current) setModuleData(data);
      })
      .catch((err) => {
        dispatch(errorHandler(err));
      });
  }, [moduleBridgeHex, subModuleBridgeHex, plantHex]);

  useEffect(() => {
    loadModuleData();
  }, [loadModuleData]);
  // > LOAD MODULE DATA //

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${moduleData.plantName} - ${ReactDOMServer.renderToString(
                    <Translator file="module-legalCompliance" name="evaluation" />,
                  )}`,
                  route: {
                    name: 'user_module_legalCompliance_evaluation',
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: `${ReactDOMServer.renderToString(
                    <Translator file="module-comsaReports" name="indicators" />,
                  )}`,
                  route: {
                    name: 'user_module_comsaReports_home',
                    params: { moduleBridgeHex, subModuleBridgeHex, plantHex },
                  },
                },
                {
                  name: `${ReactDOMServer.renderToString(
                    <Translator file="module-comsaReports" name="power.use" />,
                  )}`,
                },
              ]
            : [{ name: '...' }]
        }
      />
      <section className="content">
        <ModuleComsaSideBarReports
          activeRoute="user_module_comsaReports_power_use"
          moduleData={moduleData}
        />
        <div className="row">
          <div className="col-12">
            <Title>
              <Translator file="module-comsaReports" name="power.use" />
            </Title>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <span
              className="btn btn-success btn-sm mr-2"
              onClick={() => handleAddReport()}
              title={ReactDOMServer.renderToString(
                <Translator file="module-comsaReports" name="new.power.use" />,
              )}
            >
              <AppRemixIcon icon="add" />
            </span>

            <span
              className={`btn btn-sm btn-${
                isFiltersPanelOpen ? '' : 'outline-'
              }info float-right`}
              onClick={() => setIsFiltersPanelOpen(!isFiltersPanelOpen)}
            >
              <Translator file="module-comsaReports" name="filters" />
            </span>
            {(filters.month ||
              filters.year ||
              filters.kwUseFrom ||
              filters.kwUseTo ||
              filters.provider) && (
              <span
                className="btn btn-danger btn-sm mr-1 float-right"
                onClick={() => handleRemoveFilters()}
              >
                <AppRemixIcon icon="delete-bin-6" />
              </span>
            )}
          </div>
        </div>
        <Observations reportType={reportType} />
        <div className="row mt-3">
          <div className="col-12">
            <Collapse in={isFiltersPanelOpen}>
              <div className="card mb-0">
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <AppDatePicker
                        title={<Translator file="module-comsaReports" name="year" />}
                        value={filterYear}
                        onChange={(e) => setFilterYear(e)}
                        minDate={handleMinDate(moduleData)}
                        dateFormat="yyyy"
                        displayDateFormat="YYYY"
                        placeholder={
                          <Translator
                            file="module-comsaReports"
                            name="select.a.year"
                          />
                        }
                        isClearable
                        showYearPicker
                      />
                    </div>
                    <div className="col-md-6">
                      <AppSingleSelect
                        options={months}
                        onChange={(e) => setFilterMonth(e)}
                        title={<Translator file="module-saicaReports" name="month" />}
                        icon={<AppRemixIcon icon="calendar" />}
                        placeholder={
                          <Translator
                            file="module-comsaReports"
                            name="select.a.month"
                          />
                        }
                        value={filterMonth}
                      />
                    </div>
                    <div className="col-md-6">
                      <AppInputNumber
                        title={`${ReactDOMServer.renderToString(
                          <Translator file="module-comsaReports" name="kw.use" />,
                        )} (${ReactDOMServer.renderToString(
                          <Translator file="module-comsaReports" name="from" />,
                        )})`}
                        icon={<AppRemixIcon icon="flashlight" />}
                        value={filterKwUseFrom || ''}
                        onChange={(number) => setFilterKwUseFrom(number)}
                        decimals={3}
                      />
                    </div>
                    <div className="col-md-6">
                      <AppInputNumber
                        title={`${ReactDOMServer.renderToString(
                          <Translator file="module-comsaReports" name="kw.use" />,
                        )} (${ReactDOMServer.renderToString(
                          <Translator file="module-comsaReports" name="to" />,
                        )})`}
                        icon={<AppRemixIcon icon="flashlight" />}
                        value={filterKwUseTo || ''}
                        onChange={(number) => setFilterKwUseTo(number)}
                        decimals={3}
                      />
                    </div>
                    <div className="col-md-6">
                      <AppInput
                        title={
                          <Translator file="module-comsaReports" name="provider" />
                        }
                        icon={<AppRemixIcon icon="user-location" />}
                        onChange={(v) => setFilterProvider(v)}
                        isClearable
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12">
                      <div className="float-right">
                        <span
                          className="btn btn-info btn-sm"
                          onClick={() => handleFilters()}
                        >
                          <Translator file="module-saicaReports" name="filter" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Collapse>
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-12">
            <div className="card sr-header">
              <div className="card-body">
                {loading ? (
                  <div>
                    <Translator file="module-saicaReports" name="loading" />
                  </div>
                ) : (
                  <>
                    {reportList.length > 0 ? (
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th className="col-1">
                              <Translator file="module-comsaReports" name="year" />
                            </th>
                            <th className="col-1">
                              <Translator file="module-comsaReports" name="month" />
                            </th>
                            <th className="col-1">
                              <Translator file="module-comsaReports" name="kw.use" />
                            </th>
                            <th className="col-2">
                              <Translator
                                file="module-comsaReports"
                                name="provider"
                              />
                            </th>
                            <th className="col-1">
                              <Translator
                                file="module-comsaReports"
                                name="evidence"
                              />
                            </th>
                            <th className="col-5">
                              <Translator
                                file="module-comsaReports"
                                name="m-cr-comment"
                              />
                            </th>
                            <th className="col-1" />
                          </tr>
                        </thead>
                        <tbody>
                          {reportList &&
                            reportList.map((r) => (
                              <tr key={r.id}>
                                <td>{r.year}</td>
                                <td>
                                  <Translator
                                    file="user"
                                    name={`calendar.month${r.month}`}
                                  />
                                </td>
                                <td>{renderCommaFloat(r.kwUse)}</td>
                                <td>{r.provider}</td>
                                <td>
                                  {r.files &&
                                    Object.keys(r.files).map((salt, index) => (
                                      <span
                                        key={index}
                                        className="mr-1 pointer"
                                        title={r.files[salt].name}
                                        onClick={() =>
                                          getFile(
                                            moduleBridgeHex,
                                            subModuleBridgeHex,
                                            plantHex,
                                            reportType,
                                            r.id,
                                            salt,
                                            dispatch,
                                          )
                                        }
                                      >
                                        <AppRemixIcon icon="file-text" />
                                      </span>
                                    ))}
                                </td>
                                <td>{r.comment}</td>
                                <td className="text-center">
                                  <button
                                    type="button"
                                    className="btn btn-info btn-xs mr-1"
                                    onClick={() => handleEditReport(r)}
                                    title={ReactDOMServer.renderToString(
                                      <Translator
                                        file="module-comsaReports"
                                        name="edit"
                                      />,
                                    )}
                                    disabled={handleDisabled(moduleData, r?.year)}
                                  >
                                    <AppRemixIcon icon="edit" />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-xs"
                                    onClick={() => setDeleteReportId(r.id)}
                                    title={ReactDOMServer.renderToString(
                                      <Translator
                                        file="module-comsaReports"
                                        name="delete"
                                      />,
                                    )}
                                    disabled={handleDisabled(moduleData, r?.year)}
                                  >
                                    <AppRemixIcon icon="delete-bin-6" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="alert alert-info">
                        <Translator file="module-saicaReports" name="no.results" />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <AppModal
          show={showSendReportModal}
          title={ReactDOMServer.renderToString(
            <Translator file="module-comsaReports" name="report.form" />,
          )}
          size="lg"
          onClose={() => setShowSendReportModal(false)}
        >
          <Formik
            validateOnChange={false}
            validateOnBlur={false}
            enableReinitialize
            initialValues={reportFormInitialValues}
            validationSchema={validationSchema}
            onSubmit={handleSaveReport}
          >
            {({ setFieldValue, handleSubmit, values, errors }) => (
              <>
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <AppDatePicker
                        title={<Translator file="module-comsaReports" name="year" />}
                        value={values.year}
                        onChange={(opt) => setFieldValue('year', opt)}
                        minDate={handleMinDate(moduleData)}
                        dateFormat="yyyy"
                        displayDateFormat="YYYY"
                        placeholder={
                          <Translator
                            file="module-comsaReports"
                            name="select.a.year"
                          />
                        }
                        isClearable
                        showYearPicker
                        popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                      />
                      {errors.year && (
                        <div className="text-danger">*{errors.year}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <AppSingleSelect
                        options={months}
                        onChange={(opt) => setFieldValue('month', opt)}
                        title={<Translator file="module-saicaReports" name="month" />}
                        icon={<AppRemixIcon icon="calendar" />}
                        placeholder={
                          <Translator
                            file="module-comsaReports"
                            name="select.a.month"
                          />
                        }
                        value={values.month}
                      />
                      {errors.month && (
                        <div className="text-danger">*{errors.month}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <AppInputNumber
                        title={
                          <Translator file="module-comsaReports" name="kw.use" />
                        }
                        icon={<AppRemixIcon icon="flashlight" />}
                        value={values.kwUse}
                        onChange={(value) => setFieldValue('kwUse', value)}
                        placeholder={
                          <Translator
                            file="module-comsaReports"
                            name="enter.a.quantity"
                          />
                        }
                        decimals={3}
                      />
                      {errors.kwUse && (
                        <div className="text-danger">*{errors.kwUse}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <AppInput
                        title={
                          <Translator file="module-comsaReports" name="provider" />
                        }
                        value={values.provider}
                        icon={<AppRemixIcon icon="user-location" />}
                        onChange={(value) => setFieldValue('provider', value)}
                        isClearable
                      />
                      {errors.provider && (
                        <div className="text-danger">*{errors.provider}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-12">
                    <AppTextArea
                      title={ReactDOMServer.renderToString(
                        <Translator file="module-comsaReports" name="m-cr-comment" />,
                      )}
                      value={values?.comment}
                      onChange={(v) => setFieldValue('comment', v)}
                      height="89px"
                    />
                  </div>
                  <div className="col-sm-12 mb-3">
                    <label>
                      <Translator file="module-comsaReports" name="attach.evidence" />
                    </label>
                    <AppFileUploader
                      isMulti
                      placeholder={
                        !values.files || Object.keys(values?.files).length === 0
                          ? ReactDOMServer.renderToString(
                              <Translator
                                file="module-legalCompliance"
                                name="select.one.multiple.files"
                              />,
                            )
                          : ReactDOMServer.renderToString(
                              <Translator
                                file="general"
                                name="select.number.of.elements"
                                variables={{
                                  elements: Object.keys(values?.files).length,
                                }}
                              />,
                            )
                      }
                      onChange={(opt) => setFieldValue('files', opt)}
                      btnText={<Translator file="general" name="search..." />}
                    />
                  </div>
                </div>
                {values.id &&
                  Object.keys(reportList.find((r) => r.id === values.id).files)
                    .length > 0 && (
                    <div className="row">
                      <div className="col-12">
                        <label>
                          <Translator file="general" name="general.delete" />
                        </label>
                        <div>
                          {Object.keys(values.files).map((salt, index) => (
                            <div key={index}>
                              <label className="mb-2 pointer font-weight-light">
                                <input
                                  type="checkbox"
                                  className="mr-1"
                                  value={salt}
                                  onChange={() =>
                                    handleDeletedFiles(
                                      setFieldValue,
                                      values.deletedFiles,
                                      salt,
                                    )
                                  }
                                  checked={
                                    values.deletedFiles && values.deletedFiles[salt]
                                  }
                                />
                                {values.files[salt].name}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                <div className="row">
                  <div className="col-12">
                    <button
                      className="btn btn-success btn-block"
                      type="submit"
                      onClick={handleSubmit}
                      disabled={isSendingReport}
                    >
                      <Translator
                        file="module-comsaReports"
                        name={values.id ? 'edit' : 'add'}
                      />
                    </button>
                  </div>
                </div>
              </>
            )}
          </Formik>
        </AppModal>
        <AppModal
          show={showDeleteModal}
          type="danger"
          isConfirmBtn
          isCancelBtn
          confirmBtnText={<Translator file="module-comsaReports" name="delete" />}
          title={<Translator file="module-comsaReports" name="delete.report" />}
          onClose={handleOnDeleteModalClose}
          onConfirm={handleOnDeleteModalConfirm}
        >
          <Translator file="module-comsaReports" name="confirm.delete.report" />
        </AppModal>
      </section>
    </div>
  );
};

export default ModuleComsaPowerUseReportScreen;

const Title = styled.h1`
  text-align: center;
  font-size: 30px;
`;
